import type React from "react";
import { Fragment, useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import type { PortalActionMenuItem } from "../../../../components/ui/PortalActionMenu";
import { PortalActionMenu } from "../../../../components/ui/PortalActionMenu";
import {
  type FocusMatterItem,
  type RecommendedTalent,
  useCreateFocusMatter,
  useDeleteFocusMatter,
  useFocusMatterRecommendations,
  useFocusMatters,
  useUpdateFocusMatter,
} from "../../../../hooks/use-focus-matters-api";
import { useCreatePipeline } from "../../../../hooks/use-sales-pipelines-api";
import {
  FOCUS_MATTER_STATUSES,
  formatYenToMan,
  formatYenToManNum,
  getAffiliationLabel,
  getEmploymentTypeLabel,
  getFocusMatterStatusBadge,
  getWorkStatusBadge,
} from "../../utils";
import { FocusMatterFormModal } from "./FocusMatterFormModal";
import { TalentProgressView } from "./TalentProgressView";

const STATUSES = FOCUS_MATTER_STATUSES;

/**
 * スコアバーを表示するコンポーネント
 * @param {object} props props
 * @param {string} props.label ラベル
 * @param {number} props.score スコア (0-1)
 * @return {React.ReactElement} スコアバー
 */
const ScoreBar: React.FC<{ label: string; score: number }> = ({
  label,
  score,
}) => (
  <div className="flex items-center gap-2 text-xs">
    <span className="w-16 shrink-0 text-base-content/60">{label}</span>
    <progress
      className="progress progress-primary w-full"
      value={Math.round(score * 100)}
      max="100"
    />
    <span className="w-10 text-right">{Math.round(score * 100)}%</span>
  </div>
);

/**
 * おすすめタレントカードコンポーネント
 * @param {object} props props
 * @param {RecommendedTalent} props.talent タレント
 * @param {string} props.focusMatterId 注力案件ID
 * @param {Function} props.onAdded パイプライン追加後コールバック
 * @return {React.ReactElement} タレントカード
 */
const RecommendationCard: React.FC<{
  talent: RecommendedTalent;
  focusMatterId: string;
  onAdded: () => void;
}> = ({ talent, focusMatterId, onAdded }) => {
  const { createPipeline, loading: adding } = useCreatePipeline();
  const workStatus = getWorkStatusBadge(talent.workStatus);

  const handleAddToPipeline = useCallback(async () => {
    try {
      await createPipeline({
        talentId: talent.talentId,
        focusMatterId,
        opportunitySource: "focus_matter",
        stage: "preparation",
      });
      toast.success(`${talent.talentName} を提案候補に追加しました`);
      onAdded();
    } catch {
      toast.error("提案候補への追加に失敗しました");
    }
  }, [
    createPipeline,
    talent.talentId,
    talent.talentName,
    focusMatterId,
    onAdded,
  ]);

  return (
    <div className="card bg-base-200 shadow-sm">
      <div className="card-body p-3 gap-2">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <span className="font-medium text-sm">{talent.talentName}</span>
            <span className="text-xs text-base-content/60 ml-2">
              {getAffiliationLabel(talent.affiliation)} /{" "}
              {getEmploymentTypeLabel(talent.employmentType)}
            </span>
          </div>
          <span className={`badge badge-sm ${workStatus.className}`}>
            {workStatus.label}
          </span>
        </div>

        {/* Skills */}
        {talent.skills.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {talent.skills.map(skill => (
              <span key={skill} className="badge badge-outline badge-xs">
                {skill}
              </span>
            ))}
          </div>
        )}

        {/* Desired reward */}
        {talent.desiredReward != null && (
          <div className="text-xs text-base-content/60">
            希望単価: {formatYenToMan(talent.desiredReward)}
          </div>
        )}

        {/* Scores */}
        <div className="space-y-1 mt-1">
          <ScoreBar label="総合" score={talent.totalScore} />
          <ScoreBar label="スキル" score={talent.skillScore} />
          <ScoreBar label="単価" score={talent.rewardScore} />
          <ScoreBar label="勤務地" score={talent.locationScore} />
          <ScoreBar label="稼働" score={talent.availabilityScore} />
        </div>

        {/* Action */}
        <div className="card-actions justify-end mt-1">
          <button
            type="button"
            className="btn btn-primary btn-xs"
            disabled={adding}
            onClick={handleAddToPipeline}
          >
            {adding ? (
              <span className="loading loading-spinner loading-xs" />
            ) : (
              "提案候補に追加"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * おすすめタレントパネルコンポーネント
 * @param {object} props props
 * @param {string} props.matterId 注力案件ID
 * @return {React.ReactElement} おすすめタレントパネル
 */
const RecommendationPanel: React.FC<{ matterId: string }> = ({ matterId }) => {
  const { recommendations, loading, error, refetch } =
    useFocusMatterRecommendations(matterId);

  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <span className="loading loading-spinner loading-sm" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-error alert-sm">
        <span>おすすめタレントの取得に失敗しました</span>
      </div>
    );
  }

  if (recommendations.length === 0) {
    return (
      <div className="text-center text-base-content/60 text-sm py-4">
        おすすめタレントが見つかりませんでした
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {recommendations.map(talent => (
        <RecommendationCard
          key={talent.talentId}
          talent={talent}
          focusMatterId={matterId}
          onAdded={refetch}
        />
      ))}
    </div>
  );
};

type ViewMode = "by-deal" | "by-talent";

type SalesManagementTabProps = {
  canEdit: boolean;
  canDelete: boolean;
};

/**
 * 営業管理タブコンポーネント
 * 案件ビューと人材ビューを切り替えて表示する
 * @param {object} props プロパティ
 * @param {boolean} props.canEdit 編集権限
 * @param {boolean} props.canDelete 削除権限
 * @return {React.ReactElement} 営業管理タブ
 */
export const SalesManagementTab: React.FC<SalesManagementTabProps> = ({
  canEdit,
  canDelete,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const viewParam = searchParams.get("view");
  const viewMode: ViewMode =
    viewParam === "by-talent" ? "by-talent" : "by-deal";

  const handleViewChange = useCallback(
    (mode: ViewMode) => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.set("view", mode);
        return next;
      });
    },
    [setSearchParams],
  );

  return (
    <div>
      {/* View mode toggle */}
      <div className="flex justify-center mb-4">
        <div className="join">
          <button
            type="button"
            className={`join-item btn btn-sm ${viewMode === "by-deal" ? "btn-active" : ""}`}
            onClick={() => handleViewChange("by-deal")}
          >
            案件から見る
          </button>
          <button
            type="button"
            className={`join-item btn btn-sm ${viewMode === "by-talent" ? "btn-active" : ""}`}
            onClick={() => handleViewChange("by-talent")}
          >
            人材から見る
          </button>
        </div>
      </div>

      {viewMode === "by-deal" ? (
        <FocusMatterView canEdit={canEdit} canDelete={canDelete} />
      ) : (
        <TalentProgressView canEdit={canEdit} canDelete={canDelete} />
      )}
    </div>
  );
};

// ================================================================
// 案件ビュー（旧 FocusMatterTab の内部）
// ================================================================

type FocusMatterActionMenuProps = {
  matter: FocusMatterItem;
  canEdit: boolean;
  canDelete: boolean;
  onEdit: (matter: FocusMatterItem) => void;
  onDelete: (matter: FocusMatterItem) => void;
  onStatusChange: (
    matterId: string,
    currentStatus: string,
    newStatus: string,
  ) => void;
};

/**
 * 注力案件の操作メニュー（Portal ベース）
 * @param {object} props プロパティ
 * @param {FocusMatterItem} props.matter 案件データ
 * @param {boolean} props.canEdit 編集権限
 * @param {boolean} props.canDelete 削除権限
 * @param {Function} props.onEdit 編集コールバック
 * @param {Function} props.onDelete 削除コールバック
 * @param {Function} props.onStatusChange ステータス変更コールバック
 * @return {React.ReactElement} 操作メニュー
 */
const FocusMatterActionMenu: React.FC<FocusMatterActionMenuProps> = ({
  matter,
  canEdit,
  canDelete,
  onEdit,
  onDelete,
  onStatusChange,
}) => {
  const items = useMemo<PortalActionMenuItem[]>(() => {
    const result: PortalActionMenuItem[] = [];
    if (canEdit) {
      result.push({
        key: "edit",
        type: "button",
        label: "編集",
        onClick: () => onEdit(matter),
      });
      result.push({
        key: "status-title",
        type: "title",
        label: "ステータス変更",
      });
      for (const s of STATUSES) {
        result.push({
          key: `status-${s.value}`,
          type: "button",
          label: (
            <>
              <span
                className={`inline-block w-2.5 h-2.5 rounded-full ${s.badgeClass}`}
              />
              {s.label}
            </>
          ),
          onClick: () => onStatusChange(matter.id, matter.status, s.value),
          className: matter.status === s.value ? "active" : "",
        });
      }
    }
    if (canDelete) {
      result.push({ key: "divider", type: "divider" });
      result.push({
        key: "delete",
        type: "button",
        label: "削除",
        onClick: () => onDelete(matter),
        className: "text-error",
      });
    }
    return result;
  }, [matter, canEdit, canDelete, onEdit, onDelete, onStatusChange]);

  return (
    <PortalActionMenu items={items} onBeforeOpen={e => e.stopPropagation()} />
  );
};

type FocusMatterViewProps = {
  canEdit: boolean;
  canDelete: boolean;
};

/**
 * 案件ビューコンポーネント
 * 注力案件をテーブルで表示する
 * @param {object} props プロパティ
 * @param {boolean} props.canEdit 編集権限
 * @param {boolean} props.canDelete 削除権限
 * @return {React.ReactElement} 案件ビュー
 */
const FocusMatterView: React.FC<FocusMatterViewProps> = ({
  canEdit,
  canDelete,
}) => {
  const { matters, loading, error, refetch } = useFocusMatters();
  const { createMatter, loading: creating } = useCreateFocusMatter();
  const { updateMatter, loading: updatingMatter } = useUpdateFocusMatter();
  const { deleteMatter, loading: deletingMatter } = useDeleteFocusMatter();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<FocusMatterItem | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<FocusMatterItem | null>(
    null,
  );
  const [selectedMatterId, setSelectedMatterId] = useState<string | null>(null);

  const handleCreate = useCallback(
    async (data: Record<string, unknown>) => {
      try {
        await createMatter(data as never);
        toast.success("注力案件を追加しました");
        setIsCreateModalOpen(false);
        await refetch();
      } catch {
        toast.error("注力案件の追加に失敗しました");
      }
    },
    [createMatter, refetch],
  );

  const handleEdit = useCallback(
    async (data: Record<string, unknown>) => {
      if (!editTarget) return;
      try {
        await updateMatter(editTarget.id, data as never);
        toast.success("注力案件を更新しました");
        setEditTarget(null);
        await refetch();
      } catch {
        toast.error("注力案件の更新に失敗しました");
      }
    },
    [editTarget, updateMatter, refetch],
  );

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    try {
      await deleteMatter(deleteTarget.id);
      toast.success("注力案件を削除しました");
      setDeleteTarget(null);
      await refetch();
    } catch {
      toast.error("注力案件の削除に失敗しました");
    }
  }, [deleteTarget, deleteMatter, refetch]);

  const handleStatusChange = useCallback(
    async (matterId: string, currentStatus: string, newStatus: string) => {
      if (currentStatus === newStatus) return;
      try {
        await updateMatter(matterId, { status: newStatus } as never);
        toast.success("ステータスを更新しました");
        await refetch();
      } catch {
        toast.error("ステータスの更新に失敗しました");
      }
    },
    [updateMatter, refetch],
  );

  if (error) {
    return (
      <div className="alert alert-error">
        <span>データの取得に失敗しました: {error.message}</span>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <span className="loading loading-spinner loading-lg" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm text-base-content/60">
          {matters.length}件の注力案件
        </span>
        {canEdit && (
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => setIsCreateModalOpen(true)}
          >
            注力案件を追加
          </button>
        )}
      </div>

      {/* Table */}
      <div className="card bg-base-100 shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>案件名</th>
                <th>企業</th>
                <th>ステータス</th>
                <th>単価レンジ</th>
                <th>期限</th>
                <th>必須スキル</th>
                <th>候補者数</th>
                {(canEdit || canDelete) && <th>操作</th>}
              </tr>
            </thead>
            <tbody>
              {matters.map(matter => {
                const statusBadge = getFocusMatterStatusBadge(matter.status);
                const isExpanded = selectedMatterId === matter.id;
                return (
                  <Fragment key={matter.id}>
                    <tr
                      className={`hover cursor-pointer ${isExpanded ? "bg-primary/5" : ""}`}
                      onClick={() =>
                        setSelectedMatterId(isExpanded ? null : matter.id)
                      }
                    >
                      <td className="font-medium">{matter.title}</td>
                      <td>{matter.clientCompany || "-"}</td>
                      <td>
                        <span
                          className={`badge badge-sm whitespace-nowrap ${statusBadge.badgeClass}`}
                        >
                          {statusBadge.label}
                        </span>
                      </td>
                      <td>
                        {matter.rewardMin != null ||
                        matter.rewardMax != null ? (
                          <span className="whitespace-nowrap">
                            {matter.rewardMin != null &&
                            matter.rewardMax != null
                              ? `${formatYenToManNum(matter.rewardMin)}〜${formatYenToMan(matter.rewardMax)}`
                              : matter.rewardMin != null
                                ? formatYenToMan(matter.rewardMin)
                                : formatYenToMan(matter.rewardMax!)}
                          </span>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td>{matter.deadline || "-"}</td>
                      <td>
                        {matter.requiredSkills &&
                        matter.requiredSkills.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {matter.requiredSkills.map(skill => (
                              <span
                                key={skill}
                                className="badge badge-outline badge-xs"
                              >
                                {skill}
                              </span>
                            ))}
                          </div>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td>{matter.candidateCount}名</td>
                      {(canEdit || canDelete) && (
                        <td>
                          <FocusMatterActionMenu
                            matter={matter}
                            canEdit={canEdit}
                            canDelete={canDelete}
                            onEdit={setEditTarget}
                            onDelete={setDeleteTarget}
                            onStatusChange={handleStatusChange}
                          />
                        </td>
                      )}
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td
                          colSpan={canEdit || canDelete ? 8 : 7}
                          className="bg-base-200/50 p-4"
                        >
                          <h4 className="text-sm font-semibold mb-3">
                            AIおすすめタレント
                          </h4>
                          <RecommendationPanel matterId={matter.id} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
              {matters.length === 0 && (
                <tr>
                  <td
                    colSpan={canEdit || canDelete ? 8 : 7}
                    className="text-center py-8 text-base-content/60"
                  >
                    注力案件がありません
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create / Edit modal */}
      <FocusMatterFormModal
        key={editTarget ? `edit-${editTarget.id}` : "create"}
        isOpen={isCreateModalOpen || !!editTarget}
        onClose={() => {
          setIsCreateModalOpen(false);
          setEditTarget(null);
        }}
        onSubmit={editTarget ? handleEdit : handleCreate}
        submitting={editTarget ? updatingMatter : creating}
        initialData={editTarget ?? undefined}
      />

      {/* Delete confirm */}
      {deleteTarget && (
        <div className="modal modal-open">
          <div className="modal-box w-11/12 max-w-md">
            <h3 className="font-bold text-lg mb-4">注力案件を削除</h3>
            <p className="text-sm mb-2">この注力案件を削除しますか？</p>
            <div className="bg-base-200 rounded-lg p-3 text-sm">
              <span className="font-medium">{deleteTarget.title}</span>
              {deleteTarget.clientCompany && (
                <span className="text-base-content/60 ml-2">
                  ({deleteTarget.clientCompany})
                </span>
              )}
            </div>
            <p className="text-xs text-error mt-3">
              この操作は取り消せません。
            </p>
            <div className="modal-action">
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => setDeleteTarget(null)}
                disabled={deletingMatter}
              >
                キャンセル
              </button>
              <button
                type="button"
                className="btn btn-error btn-sm"
                onClick={handleDelete}
                disabled={deletingMatter}
              >
                {deletingMatter ? (
                  <span className="loading loading-spinner loading-xs" />
                ) : (
                  "削除"
                )}
              </button>
            </div>
          </div>
          <div
            className="modal-backdrop"
            onClick={() => !deletingMatter && setDeleteTarget(null)}
          />
        </div>
      )}
    </div>
  );
};
